/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { cloneDeep, difference, differenceWith, isEqual, uniq } from 'lodash'

import type { Cluster } from '@modules/ccm-api/v1/types'
import type {
  CloudConnectedResourceRoleAssignment,
  RoleAssignments,
} from '@modules/cloud-api/v1/types'
import { getEmptyRoleAssignments } from '@modules/role-assignments-lib'

import { CCM_ROLE_ADMIN, CCM_ROLE_VIEWER, CCM_ROLES } from './constants'

import type { CCMRole, CCMRoleAssignment, ConnectedCluster } from './types'

export function getRoleSelectOptionValue(role: string | undefined): CCMRole | undefined {
  if (CCM_ROLES.includes(role as CCMRole)) {
    return role as CCMRole
  }

  return undefined
}

export function roleAssignmentsToCCMRoleAssignmentAdapter(
  roleAssignments: RoleAssignments,
  ccmClusters: Cluster[],
): CCMRoleAssignment[] {
  return ccmClusters.map<CCMRoleAssignment>((cluster) => ({
    connectedCluster: {
      id: cluster.id,
      name: cluster.name,
      clusterUuid: cluster.self_managed_cluster.id,
    },
    roles: getRolesForCluster(cluster.id, roleAssignments),
  }))
}

export function getRolesForCluster(clusterId: string, roleAssignments: RoleAssignments): CCMRole[] {
  return (
    roleAssignments.cloud_connected_resource
      ?.filter((assignment: CloudConnectedResourceRoleAssignment) =>
        assignment.resource_ids?.includes(clusterId),
      )
      .map((assignment: CloudConnectedResourceRoleAssignment) => assignment.role_id as CCMRole) ??
    []
  )
}

export function transformRoleAssignments(
  roleAssignments: RoleAssignments,
  roleAssignment: CCMRoleAssignment,
  organizationId: string,
): RoleAssignments {
  const newCCMRoleAssignments: CloudConnectedResourceRoleAssignment[] =
    cloneDeep(roleAssignments.cloud_connected_resource) ?? []

  for (const ccmRole of CCM_ROLES) {
    const existingRoleEntry = newCCMRoleAssignments.find(
      (assignment: CloudConnectedResourceRoleAssignment) => assignment.role_id === ccmRole,
    ) ?? {
      role_id: ccmRole,
      resource_ids: [],
      organization_id: organizationId,
    }

    if (existingRoleEntry.resource_ids?.length === 0) {
      // The role entry has just been created
      newCCMRoleAssignments.push(existingRoleEntry)
    }

    if (roleAssignment.roles.includes(ccmRole)) {
      existingRoleEntry.resource_ids = uniq([
        ...(existingRoleEntry.resource_ids ?? []),
        roleAssignment.connectedCluster.id,
      ])
    } else {
      // If the role is not in the change and the role entry exists, we remove the cluster from its resource_ids
      // Otherwise, we don't need to do anything
      existingRoleEntry.resource_ids = existingRoleEntry.resource_ids?.filter(
        (id) => id !== roleAssignment.connectedCluster.id,
      )
    }

    if (existingRoleEntry.resource_ids?.length === 0) {
      // If the role entry has no resource_ids, we remove it
      newCCMRoleAssignments.splice(newCCMRoleAssignments.indexOf(existingRoleEntry), 1)
    }
  }

  return {
    ...roleAssignments,
    cloud_connected_resource: newCCMRoleAssignments,
  }
}

export const diffAllCCMClusterRoleAssignments = (
  original: CloudConnectedResourceRoleAssignment[] = [],
  changed: CloudConnectedResourceRoleAssignment[] = [],
): CCMClusterRoleAssignmentChanges => {
  const originalAllCCMClusterRoleAssignments: CloudConnectedResourceRoleAssignment[] =
    filterCCMClusterRoleAssignmentByAll(original, true)

  const changedAllCCMClusterRoleAssignments: CloudConnectedResourceRoleAssignment[] =
    filterCCMClusterRoleAssignmentByAll(changed, true)

  return {
    added: differenceWith(
      changedAllCCMClusterRoleAssignments,
      originalAllCCMClusterRoleAssignments,
      isEqual,
    ),
    removed: differenceWith(
      originalAllCCMClusterRoleAssignments,
      changedAllCCMClusterRoleAssignments,
      isEqual,
    ),
  }
}

function filterCCMClusterRoleAssignmentByAll(
  ccmClusterRoleAssignments: CloudConnectedResourceRoleAssignment[] | undefined,
  all: boolean,
): CloudConnectedResourceRoleAssignment[] {
  return (
    ccmClusterRoleAssignments
      ?.filter((assignment) => (assignment.all ?? false) === all)
      .filter(hasValidCCMRoleId)
      .sort(sortCCMClusterRoleAssignment) || []
  )
}

function hasValidCCMRoleId(
  roleAssignment: CloudConnectedResourceRoleAssignment,
): roleAssignment is CloudConnectedResourceRoleAssignment {
  return CCM_ROLES.includes(roleAssignment.role_id as CCMRole)
}

const clusterRolesOrder: Record<CCMRole, number> = {
  [CCM_ROLE_ADMIN]: 1,
  [CCM_ROLE_VIEWER]: 2,
}

function sortCCMClusterRoleAssignment(
  a: CloudConnectedResourceRoleAssignment,
  b: CloudConnectedResourceRoleAssignment,
): number {
  return clusterRolesOrder[a.role_id] - clusterRolesOrder[b.role_id]
}

type CCMClusterRoleAssignmentChanges = {
  added: CloudConnectedResourceRoleAssignment[]
  removed: CloudConnectedResourceRoleAssignment[]
}

export const diffSpecificCCMClusterRoleAssignments = (
  original: CloudConnectedResourceRoleAssignment[] = [],
  changed: CloudConnectedResourceRoleAssignment[] = [],
): CCMClusterRoleAssignmentChanges => {
  const originalSpecificCCMClusterRoleAssignments = filterCCMClusterRoleAssignmentByAll(
    original,
    false,
  )

  const originalCCMClusterIdsByRoleId = getCCMClusterIdsByRoleId(
    originalSpecificCCMClusterRoleAssignments,
  )

  const changedSpecificCCMClusterRoleAssignments = filterCCMClusterRoleAssignmentByAll(
    changed,
    false,
  )

  const changedCCMClusterIdsByRoleId = getCCMClusterIdsByRoleId(
    changedSpecificCCMClusterRoleAssignments,
  )

  const added = diffCCMClusterIds(
    changedSpecificCCMClusterRoleAssignments,
    originalCCMClusterIdsByRoleId,
  ).filter(({ resource_ids = [] }) => resource_ids.length > 0)

  const removed = diffCCMClusterIds(
    originalSpecificCCMClusterRoleAssignments,
    changedCCMClusterIdsByRoleId,
  ).filter(({ resource_ids = [] }) => resource_ids.length > 0)

  return {
    added,
    removed,
  }
}

function getCCMClusterIdsByRoleId(roleAssignments: CloudConnectedResourceRoleAssignment[]): {
  [roleId: string]: string[]
} {
  return roleAssignments.reduce(
    (prev, { role_id: roleId, resource_ids: ccmClusterIds = [] }) => ({
      ...prev,
      [roleId]: (prev[roleId] ?? []).concat(ccmClusterIds),
    }),
    {},
  )
}

export function diffCCMClusterIds(
  roleAssignments: CloudConnectedResourceRoleAssignment[],
  CCMClusterIdsByRoleId: { [roleId: string]: string[] },
): CloudConnectedResourceRoleAssignment[] {
  return roleAssignments.map<CloudConnectedResourceRoleAssignment>((roleAssignment) => ({
    ...roleAssignment,
    resource_ids: difference(
      roleAssignment.resource_ids,
      CCMClusterIdsByRoleId[roleAssignment.role_id] ?? [],
    ),
  }))
}

// Display the first 6 characters of the cluster ID for brevity and to practically ensure uniqueness in the UI.
const CLUSTER_ID_SLICE_LENGTH = 6

export function formatCCMClusterNameWithId(cluster: ConnectedCluster): string {
  return cluster.name === ''
    ? cluster.clusterUuid
    : `${cluster.name} (${cluster.clusterUuid.slice(0, CLUSTER_ID_SLICE_LENGTH)})`
}

export function formatCCMClusterA11yNameWithId(cluster: ConnectedCluster): string {
  return cluster.name === ''
    ? screenReaderId(cluster.clusterUuid)
    : `${cluster.name} (${screenReaderId(cluster.clusterUuid)})`
}

export function screenReaderId(id: string): string {
  return [...id].join(' ')
}

export const deleteCCMRoleAssignments = (roleAssignments: RoleAssignments): RoleAssignments => ({
  ...roleAssignments,
  cloud_connected_resource: getEmptyRoleAssignments().cloud_connected_resource,
})
